import { PageHero, SectionLabel } from "@/components/Bits";
import Seo from "@/components/Seo";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const VALUES = [
  { t: "Disciplined", d: "We do the unglamorous work consistently, because consistency is what produces predictable results." },
  { t: "Dependable", d: "We operate as a reliable extension of your team — the same standard, every single cycle." },
  { t: "Trustworthy", d: "We represent your brand with integrity and protect the reputation you have built." },
  { t: "Educational", d: "We share what we learn, elevating both our clients and our specialists." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About"
        description="Commercial Client Acquisition exists to make commercial insurance pipeline development a profession — governed by systems, standards, and accountability."
        path="/about"
      />
      <PageHero
        eyebrow="About CCA"
        title="Confident, disciplined, and built for the long term."
        intro="Commercial Client Acquisition exists to make commercial pipeline development a profession — governed by systems, standards, and accountability."
      />

      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-6 md:grid-cols-12 md:px-12 lg:px-16">
          <Reveal className="md:col-span-6">
            <SectionLabel num="01">Our Purpose</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-medium leading-[1.1] tracking-tight text-ink sm:text-4xl">
              Systems build results. Personalities don't scale.
            </h2>
            <div className="mt-8 space-y-5 leading-relaxed text-subtext">
              <p>
                Most agencies grow through the heroics of a few talented producers. It works — until
                it doesn't. CCA was founded on a different belief: that a disciplined, documented
                system will outperform individual talent over time, every time.
              </p>
              <p>
                We focus exclusively on commercial insurance. That focus lets us standardize the
                process, measure quality against a clear bar, and improve continuously — so our
                clients get a pipeline they can forecast against and depend on.
              </p>
            </div>
          </Reveal>
          <Reveal className="md:col-span-6" delay={0.1}>
            <div className="overflow-hidden border border-line">
              <img
                src={IMAGES.team}
                alt="CCA team collaborating on pipeline systems"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-24 text-paper md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <SectionLabel dark num="02">Brand Principles</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-medium tracking-tight sm:text-4xl">
              What we hold ourselves to.
            </h2>
          </Reveal>
          <RevealGroup className="mt-14 grid grid-cols-1 gap-px bg-inkline sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => (
              <RevealItem key={v.t} className="bg-ink p-8">
                <h3 className="font-display text-xl font-medium tracking-tight text-paper">{v.t}</h3>
                <p className="mt-3 leading-relaxed text-invtext">{v.d}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <section className="border-b border-line bg-paper py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-6 md:grid-cols-12 md:items-center md:px-12 lg:px-16">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden border border-line">
              <img
                src="https://images.unsplash.com/photo-1558222218-b7b54eede3f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="CCA leadership"
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal className="md:col-span-6 md:col-start-7" delay={0.1}>
            <SectionLabel num="03">Leadership</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-medium leading-[1.1] tracking-tight text-ink sm:text-4xl">
              Confident without arrogance.
            </h2>
            <p className="mt-6 leading-relaxed text-subtext">
              Our leadership brings decades of commercial insurance and business-development
              discipline. We lead by developing people and defending standards — the same way we
              expect our Pipeline Development Specialists to represent your agency.
            </p>
          </Reveal>
        </div>
      </section>

    </div>
  );
}
